import { isAxiosError } from "@/utils/axiosInstance";
import type { AxiosError } from "axios";

interface ThunkApiLike<RV = unknown> {
  rejectWithValue: (value: RV) => unknown;
}

const getBaseLocale = (): "pt-BR" | "pt-PT" | "en-US" => {
  if (typeof navigator !== "undefined" && navigator.language) {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith("pt-br")) return "pt-BR";
    if (lang.startsWith("pt-pt") || lang.startsWith("pt")) return "pt-PT";
    if (lang.startsWith("en")) return "en-US";
  }
  return "en-US";
};

const translateErrorMessage = (message: string): string => {
  const locale = getBaseLocale();

  if (locale === "en-US") return message;

  const map: Record<"pt-BR" | "pt-PT", Array<{ from: string; to: string }>> = {
    "pt-BR": [
      {
        from: "The email has already been taken.",
        to: "O e-mail já está em uso.",
      },
      {
        from: "The phone has already been taken.",
        to: "O telefone já está em uso.",
      },
      {
        from: "These credentials do not match our records.",
        to: "Essas credenciais não correspondem aos nossos registros.",
      },
    ],
    "pt-PT": [
      {
        from: "The email has already been taken.",
        to: "O e-mail já se encontra em uso.",
      },
      {
        from: "The phone has already been taken.",
        to: "O telefone já se encontra em uso.",
      },
      {
        from: "These credentials do not match our records.",
        to: "Estas credenciais não correspondem aos nossos registos.",
      },
    ],
  };

  const rules = map[locale];
  let translated = message;

  rules.forEach(({ from, to }) => {
    if (translated.includes(from)) {
      translated = translated.split(from).join(to);
    }
  });

  return translated;
};

export const handleThunkError = (
  error: unknown,
  thunkAPI: ThunkApiLike,
  customMessage?: string,
) => {
  if (isAxiosError(error)) {
    const axiosErr = error as AxiosError<{ message?: string; errors?: any }>;

    const data = axiosErr.response?.data;
    const serverMessage = data?.message;

    let detailedMessage: string | undefined;

    if (data?.errors && typeof data.errors === "object") {
      const errorMessages: string[] = [];
      Object.values(data.errors).forEach((fieldErrors: any) => {
        if (Array.isArray(fieldErrors)) {
          errorMessages.push(...fieldErrors.map((m) => String(m)));
        } else if (typeof fieldErrors === "string") {
          errorMessages.push(fieldErrors);
        }
      });
      if (errorMessages.length > 0) {
        detailedMessage = errorMessages.join(" ");
      }
    }

    const rawMessage =
      detailedMessage ||
      serverMessage ||
      customMessage ||
      axiosErr.message ||
      "Erro de conexão";

    const errorMessage = translateErrorMessage(rawMessage);

    // Include details object from server if present to allow richer UI messages
    const details = data?.details;

    return thunkAPI.rejectWithValue({
      error: errorMessage,
      message: errorMessage,
      details,
      status: axiosErr.response?.status,
    });
  }
  const genericMessage =
    (typeof error === "object" && error && "message" in error
      ? (error as { message?: unknown }).message
      : undefined) ||
    customMessage ||
    "An unknown error occurred";

  const errorMessage = translateErrorMessage(String(genericMessage));

  return thunkAPI.rejectWithValue({ message: errorMessage });
};
